import { Tooltip as RadixTooltip } from "radix-ui";
import { createContext, type ReactElement, type ReactNode, useContext } from "react";
import { cx } from "../../lib/cx";
import styles from "./Tooltip.module.css";

const ProviderPresent = createContext(false);

export interface TooltipProviderProps {
  children: ReactNode;
  /** Hover delay before a tooltip opens (ms). */
  delayDuration?: number;
  /** Window after closing during which the next tooltip opens instantly (ms). */
  skipDelayDuration?: number;
}

/** Mount once near the app root so tooltips share open delays. */
export function TooltipProvider({
  children,
  delayDuration = 300,
  skipDelayDuration = 300,
}: TooltipProviderProps) {
  return (
    <RadixTooltip.Provider delayDuration={delayDuration} skipDelayDuration={skipDelayDuration}>
      <ProviderPresent.Provider value={true}>{children}</ProviderPresent.Provider>
    </RadixTooltip.Provider>
  );
}

export interface TooltipProps {
  /** Supplementary text. Never the only carrier of essential information. */
  content: ReactNode;
  /** A single focusable element (button, link, …). */
  children: ReactElement;
  side?: "top" | "right" | "bottom" | "left";
  align?: "start" | "center" | "end";
  open?: boolean;
  defaultOpen?: boolean;
  onOpenChange?: (open: boolean) => void;
  /** Overrides the provider delay for this tooltip (ms). */
  delayDuration?: number;
  className?: string;
}

/**
 * Hover/focus hint for a control. Opens on keyboard focus too and closes with Escape.
 * Works without `TooltipProvider` (it falls back to a local one).
 */
export function Tooltip({
  content,
  children,
  side = "top",
  align = "center",
  open,
  defaultOpen,
  onOpenChange,
  delayDuration,
  className,
}: TooltipProps) {
  const hasProvider = useContext(ProviderPresent);
  const tooltip = (
    <RadixTooltip.Root
      open={open}
      defaultOpen={defaultOpen}
      onOpenChange={onOpenChange}
      delayDuration={delayDuration}
    >
      <RadixTooltip.Trigger asChild>{children}</RadixTooltip.Trigger>
      <RadixTooltip.Portal>
        <RadixTooltip.Content
          side={side}
          align={align}
          sideOffset={6}
          collisionPadding={8}
          className={cx(styles.content, className)}
        >
          {content}
          <RadixTooltip.Arrow className={styles.arrow} width={10} height={5} />
        </RadixTooltip.Content>
      </RadixTooltip.Portal>
    </RadixTooltip.Root>
  );
  return hasProvider ? tooltip : <TooltipProvider>{tooltip}</TooltipProvider>;
}
